import { CSSProperties, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { ClipboardStyled } from '@backrs_ui/grow-ui/src/components/clipboard/ClipboardStyled';
import { CopyButton } from '@backrs_ui/grow-ui/src/components/clipboard/CopyButton';
import type {
  CollegeShortlistCollege,
  CollegeShortlistPayload,
} from '@backrs_ui/grow-ui/src/components/Lubav/streamTypes';
import { useGetCollegeByInstitutionIdQuery } from '@backrs_ui/grow-ui/src/redux/college/college.api';

import {
  Box,
  Chip,
  Skeleton,
  Typography,
  SxProps,
  Theme,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';

type SchoolCardProps = Readonly<{
  school: CollegeShortlistCollege;
  variant: 'summary' | 'detail';
  highlightsLabel?: string;
  considerationsLabel?: string;
}>;

const SchoolCard = ({ school, variant, highlightsLabel, considerationsLabel }: SchoolCardProps) => {
  const navigate = useNavigate();
  const { data: collegeData, isLoading } = useGetCollegeByInstitutionIdQuery(String(school.id), {
    skip: !school.id,
  });

  const schoolName = collegeData?.school_name || school.name || '';

  const handleChipClick = () => {
    if (collegeData?.id) {
      navigate(`${APP_ROUTE.UNIVERSITY}/${collegeData.id}`);
    }
  };

  if (variant === 'summary') {
    if (isLoading) {
      return <Skeleton variant="text" width="80%" height={20} />;
    }

    return (
      <Typography
        variant="text-sm-regular"
        color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
      >
        {schoolName}
      </Typography>
    );
  }

  if (isLoading) {
    return (
      <Box sx={schoolDetailContainerSx}>
        <Skeleton variant="rounded" width={150} height={28} />
        <Skeleton variant="text" width="100%" height={60} />
      </Box>
    );
  }

  return (
    <Box sx={schoolDetailContainerSx}>
      <Chip
        id={`college-shortlist-chip-${school.id}`}
        variant="badge"
        label={<ClipboardStyled style={copyHeaderStyle}>{schoolName}</ClipboardStyled>}
        size="md"
        sx={schoolChipSx}
        type="leading"
        icon={
          <img
            src={collegeData?.logo_media_url || '/images/university-logo-default.svg'}
            alt=""
            width={16}
            height={16}
            style={chipIconStyle}
          />
        }
        onClick={handleChipClick}
      />
      {school.relevance && (
        <Typography variant="text-md-regular" sx={paragraphSx}>
          {school.relevance}
        </Typography>
      )}
      {school.highlights && highlightsLabel && (
        <Typography variant="text-md-regular" sx={paragraphSx}>
          <Typography component="span" variant="text-md-semibold">
            {highlightsLabel}{' '}
          </Typography>
          {school.highlights}
        </Typography>
      )}
      {school.considerations && considerationsLabel && (
        <Typography variant="text-md-regular" sx={paragraphSx}>
          <Typography component="span" variant="text-md-semibold">
            {considerationsLabel}{' '}
          </Typography>
          {school.considerations}
        </Typography>
      )}
    </Box>
  );
};

type CollegeShortlistRendererProps = Readonly<{
  payload: CollegeShortlistPayload;
}>;

export const CollegeShortlistRenderer = ({ payload }: CollegeShortlistRendererProps) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const strings = appStrings.modules.officeHours.infoBox.collegeShortlist;
  const categories = [
    {
      key: 'safe' as const,
      title: strings.safety,
      detailTitle: strings.safetySchools,
      schools: payload.safe,
    },
    {
      key: 'target' as const,
      title: strings.target,
      detailTitle: strings.targetSchools,
      schools: payload.target,
    },
    {
      key: 'stretch' as const,
      title: strings.reach,
      detailTitle: strings.reachSchools,
      schools: payload.stretch,
    },
  ];

  return (
    <Box>
      <Box sx={copyButtonRowSx}>
        <CopyButton id="college-shortlist-copy-button" contentRef={contentRef} />
      </Box>
      <Box ref={contentRef}>
        <Box sx={summaryContainerSx}>
          <Table sx={tableSx}>
            <TableHead>
              <TableRow>
                {categories.map(({ key, title }) => (
                  <TableCell key={key} sx={headerCellSx}>
                    <Typography
                      variant="text-lg-semibold"
                      color={({ backrs }) => backrs.colors['Colors/Text/text-primary']}
                    >
                      {title}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                {categories.map(({ key, schools }) => (
                  <TableCell key={key} sx={summaryCellSx}>
                    <Box sx={schoolListSx}>
                      {schools && schools.length > 0
                        ? schools.map((school, i) => (
                            <SchoolCard key={i} school={school} variant="summary" />
                          ))
                        : null}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </Box>
        {categories.map(({ key, detailTitle, schools }) => {
          if (!schools?.length) return null;

          return (
            <Box key={key} sx={categoryDetailContainerSx}>
              <Box sx={categoryDetailHeaderContainerSx}>
                <Typography
                  variant="text-sm-semibold"
                  color={({ backrs }) => backrs.colors['Colors/Text/text-quaternary']}
                >
                  {detailTitle}
                </Typography>
                <Divider sx={dividerSx} />
              </Box>
              {schools.map((school, i) => (
                <ClipboardStyled key={i} style={copySchoolSpacingStyle}>
                  <SchoolCard
                    school={school}
                    variant="detail"
                    highlightsLabel={strings.highlights}
                    considerationsLabel={strings.considerations}
                  />
                </ClipboardStyled>
              ))}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const summaryContainerSx: SxProps<Theme> = ({ backrs }) => ({
  marginBottom: backrs.spacing['spacing-4xl'],
});

const tableSx: SxProps<Theme> = {
  width: '100%',
  borderCollapse: 'collapse',
  tableLayout: 'fixed',
};

const headerCellSx: SxProps<Theme> = ({ backrs }) => ({
  paddingRight: backrs.spacing['spacing-xl'],
  paddingLeft: 0,
  paddingTop: 0,
  paddingBottom: backrs.spacing['spacing-sm'],
  border: 'none',
  width: '33.33%',
});

const summaryCellSx: SxProps<Theme> = ({ backrs }) => ({
  verticalAlign: 'top',
  paddingRight: backrs.spacing['spacing-xl'],
  paddingLeft: 0,
  paddingTop: 0,
  paddingBottom: 0,
  border: 'none',
  width: '33.33%',
});

const schoolListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const categoryDetailContainerSx: SxProps<Theme> = ({ backrs }) => ({
  marginBottom: backrs.spacing['spacing-3xl'],
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
});

const schoolDetailContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const schoolChipSx: SxProps<Theme> = ({ backrs, typography }) => ({
  width: 'fit-content',
  backgroundColor: backrs.colors['Colors/Base/White'],
  border: `1px solid ${backrs.colors['Gray/700']}`,
  paddingLeft: backrs.spacing['spacing-xxs'],
  paddingRight: backrs.spacing['spacing-sm'],
  '& .MuiChip-label': {
    ...typography['text-sm-medium'],
    color: backrs.colors['Gray/700'],
  },
  gap: backrs.spacing['spacing-lg'],
  cursor: 'pointer',
});

const paragraphSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/600'],
});

const categoryDetailHeaderContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
  my: backrs.spacing['spacing-md'],
});

const dividerSx: SxProps<Theme> = ({ backrs }) => ({
  borderColor: backrs.colors['Colors/Border/border-secondary'],
});

const copyButtonRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginBottom: backrs.spacing['spacing-sm'],
});

const copyHeaderStyle: CSSProperties = {
  fontWeight: '700',
  fontSize: '16px',
};

const copySchoolSpacingStyle: CSSProperties = {
  display: 'block',
  marginBottom: '16px',
};

const chipIconStyle: CSSProperties = {
  borderRadius: '50%',
  border: '.75px solid black',
};
